import { DatePipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Producer } from '@homeleite/domain';
import { IntegrationApiService } from '../../../../../../../../libs/domain/src/lib/integration/integration-api.service';
import { lastValueFrom, Subscription } from 'rxjs';

type SyncState = 'idle' | 'running' | 'success' | 'error';

@Component({
    selector: 'homeleite-producer-details-synchronize',
    templateUrl: './producer-details-synchronize.component.html',
    styleUrls: ['./producer-details-synchronize.component.scss'],
    providers: [DatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ProducerDetailsSynchronizeComponent implements OnInit, OnDestroy {
    @Input() entity: Producer;

    public form!: FormGroup;
    public isSyncing = false;
    public startDate = Date.now();
    public syncState: SyncState = 'idle';
    public statusTitle = '';
    public statusMessage = '';
    public step = 0;
    private stepTimer?: ReturnType<typeof setInterval>;
    private sub?: Subscription;

    constructor(
        private changeDetect: ChangeDetectorRef,
        private datePipe: DatePipe,
        private fb: FormBuilder,
        private integrationApiService: IntegrationApiService,
        private snackBar: MatSnackBar
    ) {}

    ngOnInit() {
        this.form = this.fb.group({
            syncDate: ['']
        });
    }

    ngOnDestroy() {
        this.clearStepTimer();
        this.sub?.unsubscribe();
    }

    get producerCode(): string {
        return (this.entity?.code || '').toString().trim();
    }

    get statusPill(): string {
        if (this.syncState === 'running') return 'Em andamento';
        if (this.syncState === 'success') return 'Concluído';
        if (this.syncState === 'error') return 'Falhou';
        return '';
    }

    clearDate(): void {
        this.form.patchValue({ syncDate: '' });
    }

    async initSync() {
        const code = this.producerCode;
        if (!code) {
            this.snackBar.open('Informe o código do produtor para sincronizar.', 'Fechar', {
                duration: 4000
            });
            return;
        }

        this.isSyncing = true;
        this.syncState = 'running';
        this.step = 1;
        this.statusTitle = 'Sincronização iniciada';
        this.statusMessage = 'Enviando solicitação para a integração…';
        this.changeDetect.detectChanges();
        this.startStepProgress();

        const dateValue = this.form.get('syncDate')?.value;
        const since = dateValue
            ? this.datePipe.transform(dateValue, 'yyyy-MM-dd-HH-mm-ss')
            : undefined;

        try {
            const status = await lastValueFrom(
                this.integrationApiService.fetchAndWait(code, since || undefined)
            );

            this.clearStepTimer();
            this.step = 3;

            if (status.toUpperCase() !== 'SUCCESS') {
                this.syncState = 'error';
                this.statusTitle = 'Sincronização não concluída';
                this.statusMessage =
                    'A integração retornou status diferente de sucesso. Contate o suporte se o problema persistir.';
                this.snackBar.open('A sincronização falhou.', 'Fechar', { duration: 6000 });
            } else {
                this.syncState = 'success';
                this.statusTitle = 'Sincronização finalizada';
                this.statusMessage = since
                    ? `Dados atualizados a partir de ${this.datePipe.transform(dateValue, 'dd/MM/yyyy')}.`
                    : 'Histórico completo sincronizado com sucesso.';
                this.snackBar.open('Sincronização finalizada.', 'Fechar', { duration: 5000 });
            }
        } catch (err) {
            console.error('Erro ao iniciar a sincronização:', err);
            this.clearStepTimer();
            this.syncState = 'error';
            this.statusTitle = 'Erro ao sincronizar';
            this.statusMessage = 'Não foi possível concluir a sincronização. Tente novamente.';
            this.snackBar.open('Erro ao iniciar a sincronização.', 'Fechar', { duration: 5000 });
        } finally {
            this.isSyncing = false;
            this.changeDetect.detectChanges();
        }
    }

    private startStepProgress(): void {
        this.clearStepTimer();
        this.stepTimer = setInterval(() => {
            if (this.step < 2) {
                this.step = 2;
                this.statusMessage = 'Aguardando processamento na integração…';
                this.changeDetect.detectChanges();
            }
        }, 6000);
    }

    private clearStepTimer(): void {
        if (this.stepTimer) {
            clearInterval(this.stepTimer);
            this.stepTimer = undefined;
        }
    }
}
